import { setContext } from "@apollo/client/link/context";
import { useRouter } from "next/router";

export const WORKSPACE_ID_HEADER = "X-Speria-Workspace-Id";
export const WORKSPACE_PATH_SEGMENT = "w";

const MAX_WORKSPACE_ID_LENGTH = 128;
const SAFE_WORKSPACE_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

export const parseWorkspaceId = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  if (value.length === 0 || value.length > MAX_WORKSPACE_ID_LENGTH) return null;
  if (!SAFE_WORKSPACE_ID_PATTERN.test(value)) return null;

  return value.toLowerCase();
};

export const parseWorkspaceIdFromPathname = (
  pathname: string,
): string | null => {
  const match = pathname.match(/^\/w\/([^/?#]+)(?:[/?#]|$)/);
  return parseWorkspaceId(match?.[1]);
};

export const withWorkspacePrefix = (
  workspaceId: string,
  path: string,
): string => {
  const normalizedWorkspaceId = parseWorkspaceId(workspaceId);
  if (normalizedWorkspaceId == null) {
    throw new Error("Invalid workspace ID.");
  }
  if (!path.startsWith("/")) {
    throw new Error("Path must start with '/'.");
  }
  if (/^\/w(?:\/|$)/.test(path)) {
    throw new Error("Path already has a workspace prefix.");
  }

  return `/${WORKSPACE_PATH_SEGMENT}/${normalizedWorkspaceId}${path}`;
};

export const createWorkspaceHeaderLink = (workspaceId: string) => {
  const normalizedWorkspaceId = parseWorkspaceId(workspaceId);
  if (normalizedWorkspaceId == null) {
    throw new Error("Invalid workspace ID.");
  }

  return setContext((_, { headers }) => ({
    headers: {
      ...headers,
      [WORKSPACE_ID_HEADER]: normalizedWorkspaceId,
    },
  }));
};

export const useWorkspaceIdFromRouter = (): {
  isReady: boolean;
  workspaceId: string | null;
} => {
  const router = useRouter();
  return {
    isReady: router.isReady,
    workspaceId: parseWorkspaceId(router.query.workspaceId),
  };
};
